/*
 * Bella Mia (14.10 SWAP+B RB-CART, cart-header-spec 4b + 4b-1): the true gift timer of the cart drawer.
 * Markup: snippets/bm-cart-timer.liquid. CSS: snippets/cart-drawer.liquid.
 *
 * - 10:00 from the moment the drawer first sees a pair with its gifts; saved in localStorage 'bm_gift_timer'
 *   ({t: cart token, end: ms}), so a refresh or a reopened drawer continues the same count.
 * - 00:00: localStorage 'bm_gifts_released' ({t: cart token, g: [kinds]}) is set FIRST, then the gift lines are really
 *   removed with one /cart/update.js (the _bm_gift Velvet, the map gift, the mystery gift; never the pair or the
 *   insurance). snippets/bm-cart-gift-guard.liquid does not re-add the map gift while the flag is set for this cart.
 * - Released: "המתנות שוחררו מהעגלה" + the gold button; one tap re-adds the released gifts (_bm_gift "pair", no
 *   selling plan, like assets/bm-pdp-bundle.js), clears the flag and restarts 10:00.
 * - The flag and the timer are cleared when the pair leaves or the cart empties (also by the gift guard), and a new
 *   pair add after the release (the PDP bundle re-adds the gifts) restarts 10:00.
 * 15.10 ROUND E CART (R2): the pair's Velvet is the gift Velvet product "מכחול Velvet במתנה" (velvet-gift, data-brush-id);
 *   a restore adds it without a plan (the gift guard gives it and the mystery gift plan 15406825670 right after, while the
 *   pair has a plan). A gift Velvet marked _bm_gift "own" replaced her own paid Velvet: it is never released.
 * Every cart request carries the cart-items section ids and resolves Horizon's CartLinesUpdateEvent from document,
 * so the drawer, /cart and the header count morph from the response.
 */
const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
const STATE = 'bm_gift_timer';
const FLAG = 'bm_gifts_released';
const PRODUCT = 'temporary-eye-tightener';
const PAIR_IDS = [48283860435142];
const PAIR_VALUES = ['זוג', 'זוג שפורפרות', 'המארז המלא'];
const JSON_HEADERS = { 'Content-Type': 'application/json', Accept: 'application/json' };
const FEEDBACK_MS = 1500;

/** Seconds of the count. 600 on the store; a test can shorten it only in its own browser context. */
const seconds = () => {
  const test = Number(/** @type {any} */ (window).__bmCartTimerTestSeconds);
  return test > 0 ? test : 600;
};

const store = {
  /** @param {string} key */
  get(key) {
    try {
      return JSON.parse(window.localStorage.getItem(key) || 'null');
    } catch (error) {
      return null;
    }
  },
  /** @param {string} key @param {any} value */
  set(key, value) {
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      // storage blocked: the count still runs for this page
    }
  },
  /** @param {string} key */
  del(key) {
    try {
      window.localStorage.removeItem(key);
    } catch (error) {
      // nothing to clear
    }
  },
};

const clearAll = () => {
  store.del(STATE);
  store.del(FLAG);
};

/** @type {Promise<any> | null} */
let eventsModule = null;
const loadEvents = () => eventsModule || (eventsModule = import('@shopify/events').catch(() => null));

/** @param {any} item */
const isPair = (item) =>
  Boolean(item) &&
  item.handle === PRODUCT &&
  !(item.properties && item.properties._bm_gift) &&
  (PAIR_IDS.includes(item.variant_id) ||
    PAIR_VALUES.some(
      (value) =>
        (item.variant_options || []).includes(value) ||
        (item.options_with_values || []).some((/** @type {any} */ o) => o.value === value)
    ));

/** The gift kind of a cart line, or '' for a line that is not a gift. @param {any} item */
const giftKind = (item) => {
  if (!item || item.handle === PRODUCT) return '';
  if (item.handle === 'mystery-gift') return 'mystery';
  if (item.handle === 'under-eye-map-gift') return 'map';
  // 15.10 ROUND E CART (R2): the gift Velvet product; one that replaced her own paid Velvet ("own") is never released
  if (item.handle === 'velvet-gift') return item.properties && item.properties._bm_gift === 'own' ? '' : 'brush';
  if (item.properties && item.properties._bm_gift) return item.handle === 'applicator-brush' ? 'brush' : 'other';
  return '';
};

const reducedMotion = () => {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch (error) {
    return false;
  }
};

async function getCart() {
  const response = await fetch(`${ROOT}cart.js`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
    cache: 'no-store',
  });
  if (!response.ok) throw new Error(`cart.js ${response.status}`);
  return response.json();
}

function sectionIds() {
  return Array.from(
    new Set(
      Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
        .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : ''))
        .filter(Boolean)
    )
  );
}

/**
 * @param {string} url
 * @param {Record<string, any>} body
 */
async function post(url, body) {
  const ids = sectionIds();
  const payload = ids.length ? { ...body, sections: ids.join(','), sections_url: window.location.pathname } : body;
  const response = await fetch(url, {
    method: 'POST',
    headers: JSON_HEADERS,
    credentials: 'same-origin',
    body: JSON.stringify(payload),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok || (data && data.status)) {
    throw new Error((data && (data.description || data.message)) || `cart request failed ${response.status}`);
  }
  return data;
}

/**
 * Resolves one CartLinesUpdateEvent from document with the sections of the last request.
 * @param {any} data the last cart response (carries the sections)
 * @param {any} cart a full cart object
 * @param {Array<Record<string, any>>} lines
 */
async function announce(data, cart, lines) {
  const mod = await loadEvents();
  const LinesUpdate = mod && mod.CartLinesUpdateEvent;
  if (!LinesUpdate) {
    if (sectionIds().length) window.location.reload();
    return;
  }
  const deferred = LinesUpdate.createPromise();
  document.dispatchEvent(new LinesUpdate({ action: 'update', context: 'cart', lines, promise: deferred.promise }));
  deferred.resolve({
    cart: LinesUpdate.createCartFromAjaxResponse(cart),
    detail: {
      source: 'bm-cart-timer',
      sections: (data && data.sections) || undefined,
      items: cart.items,
      itemCount: cart.item_count,
      didError: false,
    },
  });
}

class BmCartTimer extends HTMLElement {
  static get observedAttributes() {
    return ['data-sig'];
  }

  constructor() {
    super();
    this.seq = 0;
    /** @type {{ t: string, end: number } | null} */
    this.state = null;
    /** @type {ReturnType<typeof setTimeout> | null} */
    this.tickTimer = null;
    /** @type {ReturnType<typeof setTimeout> | null} */
    this.doneTimer = null;
    /** @type {ReturnType<typeof setTimeout> | null} */
    this.retryTimer = null;
    this.expiring = false;
    this.busy = false;
    this.mode = '';
    this.onClick = this.onClick.bind(this);
    this.onVisible = this.onVisible.bind(this);
  }

  connectedCallback() {
    this.addEventListener('click', this.onClick);
    document.addEventListener('visibilitychange', this.onVisible);
    const drawer = this.closest('theme-drawer');
    if (drawer && 'MutationObserver' in window) {
      // 4b-1.6: reopening the drawer after the release shows the bar with the button first, without scrolling
      this.drawerObserver = new MutationObserver(() => {
        if (drawer.hasAttribute('open') && this.mode === 'released') this.scrollTop();
      });
      this.drawerObserver.observe(drawer, { attributes: true, attributeFilter: ['open'] });
    }
    this.prime();
    this.sync();
  }

  /** First paint from the saved state (the rendered 10:00 is only a placeholder); sync() then reads /cart.js. */
  prime() {
    if (!(Number(this.dataset.pair) > 0)) return;
    const gifts = Number(this.dataset.gifts) > 0;
    const saved = store.get(STATE);
    const flag = store.get(FLAG);
    if (gifts && saved && Number(saved.end) > Date.now()) {
      this.render('count', Math.ceil((Number(saved.end) - Date.now()) / 1000));
    } else if (!gifts && flag && flag.t) {
      this.render('released');
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    document.removeEventListener('visibilitychange', this.onVisible);
    if (this.drawerObserver) this.drawerObserver.disconnect();
    this.stopTick();
    if (this.doneTimer) clearTimeout(this.doneTimer);
    if (this.retryTimer) clearTimeout(this.retryTimer);
  }

  /** @param {string} name @param {string | null} oldValue @param {string | null} newValue */
  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue !== null && oldValue !== newValue && this.isConnected) this.sync();
  }

  onVisible() {
    if (document.visibilityState === 'visible' && this.mode === 'count') this.tick();
  }

  /** @param {MouseEvent} event */
  onClick(event) {
    const target = event.target instanceof Element ? event.target.closest('[data-bm-ctimer-restore]') : null;
    if (!(target instanceof HTMLButtonElement)) return;
    event.preventDefault();
    this.restore(target);
  }

  /** @param {string} selector */
  part(selector) {
    return /** @type {HTMLElement | null} */ (this.querySelector(selector));
  }

  scrollTop() {
    const body = this.closest('.cart-drawer__items');
    if (body && typeof body.scrollTo === 'function') body.scrollTo({ top: 0, behavior: reducedMotion() ? 'auto' : 'smooth' });
  }

  stopTick() {
    if (this.tickTimer) clearTimeout(this.tickTimer);
    this.tickTimer = null;
  }

  /**
   * @param {'none' | 'count' | 'released'} mode
   * @param {number} [left] seconds left
   */
  render(mode, left) {
    const root = this.part('[data-bm-ctimer]');
    const count = this.part('[data-bm-ctimer-count]');
    const rel = this.part('[data-bm-ctimer-rel]');
    const mmss = this.part('[data-bm-ctimer-mmss]');
    if (!root || !count || !rel) return;
    const was = this.mode;
    this.mode = mode;
    root.hidden = mode === 'none';
    root.classList.toggle('is-released', mode === 'released');
    count.hidden = mode !== 'count';
    rel.hidden = mode !== 'released';
    if (mode === 'count' && mmss && typeof left === 'number') {
      const s = Math.max(0, left);
      mmss.textContent = `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
    }
    if (mode === 'released' && was !== 'released') {
      const live = this.part('[data-bm-ctimer-live]');
      if (live) live.textContent = 'המתנות שוחררו מהעגלה';
    }
  }

  /** @param {string} token */
  start(token) {
    this.state = { t: token, end: Date.now() + seconds() * 1000 };
    store.set(STATE, this.state);
  }

  tick() {
    this.stopTick();
    if (!this.state) return;
    const msLeft = this.state.end - Date.now();
    const left = Math.ceil(msLeft / 1000);
    if (left <= 0) {
      this.render('count', 0);
      this.expire();
      return;
    }
    this.render('count', left);
    const next = msLeft - (left - 1) * 1000;
    this.tickTimer = setTimeout(() => this.tick(), Math.max(50, Math.min(1000, next)));
  }

  async sync() {
    const seq = ++this.seq;
    if (!(Number(this.dataset.pair) > 0)) {
      // no pair in the rendered cart: no bar, nothing to keep
      this.stopTick();
      this.state = null;
      clearAll();
      this.render('none');
      return;
    }
    let cart = null;
    try {
      cart = await getCart();
    } catch (error) {
      return;
    }
    if (seq !== this.seq || !this.isConnected) return;
    const token = String(cart.token || '');
    const items = Array.isArray(cart.items) ? cart.items : [];
    if (!items.some(isPair)) {
      this.stopTick();
      this.state = null;
      clearAll();
      this.render('none');
      return;
    }
    const gifts = items.filter((item) => giftKind(item));
    const flag = store.get(FLAG);
    if (flag && flag.t === token) {
      if (!gifts.length) {
        this.stopTick();
        this.state = null;
        store.del(STATE);
        this.render('released');
        return;
      }
      // the gifts are back (a new pair add after the release): a new 10:00
      store.del(FLAG);
      this.start(token);
    } else if (flag) {
      store.del(FLAG);
    }
    if (!gifts.length) {
      // a pair without gift lines that were never released here (e.g. her own Velvet made free): no bar
      this.stopTick();
      this.state = null;
      store.del(STATE);
      this.render('none');
      return;
    }
    const saved = store.get(STATE);
    if (saved && saved.t === token && Number(saved.end) > 0) this.state = { t: token, end: Number(saved.end) };
    else if (!this.state || this.state.t !== token) this.start(token);
    this.tick();
  }

  async expire() {
    if (this.expiring) return;
    this.expiring = true;
    let retry = false;
    try {
      const cart = await getCart();
      const token = String(cart.token || '');
      const items = Array.isArray(cart.items) ? cart.items : [];
      if (!items.some(isPair)) {
        this.state = null;
        clearAll();
        this.render('none');
        return;
      }
      const gifts = items.filter((item) => giftKind(item));
      const kinds = Array.from(new Set(gifts.map(giftKind)));
      const previous = store.get(FLAG);
      const keep = previous && previous.t === token && Array.isArray(previous.g) ? previous.g : [];
      // the flag goes in BEFORE the removal, so the gift guard never re-adds the map gift in between
      store.set(FLAG, { t: token, g: Array.from(new Set(keep.concat(kinds))), at: Date.now() });
      store.del(STATE);
      this.state = null;
      this.render('released');
      if (!gifts.length) return;
      /** @type {Record<string, number>} */
      const updates = {};
      for (const item of gifts) updates[item.key] = 0;
      const data = await post(`${ROOT}cart/update.js`, { updates });
      await announce(
        data,
        data && Array.isArray(data.items) ? data : await getCart(),
        gifts.map((item) => ({ id: item.key, quantity: 0 }))
      );
    } catch (error) {
      console.warn('[bm-cart-timer]', error);
      retry = true;
    } finally {
      this.expiring = false;
    }
    if (retry) {
      // the flag stays set (no re-add); the removal is tried again shortly
      if (this.retryTimer) clearTimeout(this.retryTimer);
      this.retryTimer = setTimeout(() => this.retryRelease(), 8000);
    }
  }

  async retryRelease() {
    try {
      const cart = await getCart();
      const items = Array.isArray(cart.items) ? cart.items : [];
      if (!items.some(isPair)) {
        clearAll();
        this.render('none');
        return;
      }
      if (items.some((item) => giftKind(item))) await this.expire();
    } catch (error) {
      // offline: the next sync tries again
    }
  }

  /** @param {HTMLButtonElement} button */
  async restore(button) {
    if (this.busy) return;
    this.busy = true;
    // 4b-1.5: the bar goes back to the full count at once, with the feedback, and the body scrolls to the top;
    // the requests follow (a failure shows the button again)
    this.stopTick();
    this.render('count', seconds());
    this.feedback();
    this.scrollTop();
    let restored = false;
    try {
      const cart = await getCart();
      const token = String(cart.token || '');
      const items = Array.isArray(cart.items) ? cart.items : [];
      if (!items.some(isPair)) {
        clearAll();
        this.render('none');
        return;
      }
      const flag = store.get(FLAG);
      const want = flag && flag.t === token && Array.isArray(flag.g) && flag.g.length ? flag.g : ['brush', 'mystery', 'map'];
      const brushId = Number(this.dataset.brushId) || 0;
      const mysteryId = Number(this.dataset.mysteryId) || 0;
      const mapId = Number(this.dataset.mapId) || 0;
      const gate = this.dataset.gate === 'on';
      /** @type {Array<{ id: number, quantity: number, properties: Record<string, string> }>} */
      const add = [];
      const gift = { _bm_gift: 'pair' };
      // the same rules as the PDP bundle: one Velvet unit (D2 makes it free with the pair), one mystery gift, one map gift
      if (want.includes('brush') && brushId && !items.some((item) => item.variant_id === brushId))
        add.push({ id: brushId, quantity: 1, properties: gift });
      if (want.includes('mystery') && mysteryId && !items.some((item) => item.handle === 'mystery-gift' || item.variant_id === mysteryId))
        add.push({ id: mysteryId, quantity: 1, properties: gift });
      if (want.includes('map') && gate && mapId && !items.some((item) => item.handle === 'under-eye-map-gift' || item.variant_id === mapId))
        add.push({ id: mapId, quantity: 1, properties: gift });

      let data = null;
      if (add.length) {
        try {
          data = await post(`${ROOT}cart/add.js`, { items: add });
        } catch (error) {
          // one gift not available any more: add the others one by one
          for (const one of add) {
            try {
              data = await post(`${ROOT}cart/add.js`, { items: [one] });
            } catch (oneError) {
              console.warn('[bm-cart-timer]', oneError);
            }
          }
          if (!data) throw error;
        }
      }
      store.del(FLAG);
      this.start(token);
      this.tick();
      restored = true;
      if (data) {
        const fresh = await getCart();
        await announce(data, fresh, add.map((one) => ({ merchandiseId: String(one.id), quantity: one.quantity })));
      }
    } catch (error) {
      console.warn('[bm-cart-timer]', error);
    } finally {
      this.busy = false;
      if (!restored && this.mode === 'count' && !this.state) {
        const done = this.part('[data-bm-ctimer-done]');
        if (done) done.hidden = true;
        this.render('released');
      }
    }
  }

  feedback() {
    const done = this.part('[data-bm-ctimer-done]');
    const live = this.part('[data-bm-ctimer-live]');
    if (live) live.textContent = 'המתנות חזרו לעגלה';
    if (!done) return;
    done.hidden = false;
    if (this.doneTimer) clearTimeout(this.doneTimer);
    this.doneTimer = setTimeout(() => {
      done.hidden = true;
    }, FEEDBACK_MS);
  }
}

if (!customElements.get('bm-cart-timer')) customElements.define('bm-cart-timer', BmCartTimer);
